import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { getApiBase, getAuthHeaders } from '../../utils/api';
import {
  CreditCard,
  FileText,
  DollarSign,
  Plus,
  Trash2,
  Check,
  X,
  Loader2,
  AlertCircle,
  Building2,
  Calendar,
  Send,
  Eye,
  RefreshCw,
  Star,
  Download,
  Receipt,
  RotateCcw,
  Landmark,
  Edit3,
  ArrowUpDown,
  Banknote,
} from 'lucide-react';
import RefundRequestModal from './RefundRequestModal';
import RefundList from './RefundList';
import RefundDetails from './RefundDetails';

// Initialize Stripe - use test key for sandbox
// Try multiple env var names for flexibility
const stripePublishableKey =
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY ||
  process.env.REACT_APP_STRIPE_TEST_PUBLISHABLE_KEY ||
  'pk_test_placeholder';
const stripePromise = loadStripe(stripePublishableKey);

/**
 * BillingDashboard - Client organization billing management UI
 *
 * Features:
 * - Billing profile management
 * - View invoices (read-only - invoices are created by admins)
 * - Payment method management (Stripe)
 * - Invoice payment processing
 */
const BillingDashboard = ({ currentUser }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Data states
  const [billingProfile, setBillingProfile] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [billingSubTab, setBillingSubTab] = useState('invoices'); // 'invoices' or 'receipts'

  // Form states
  const [showAddPaymentMethod, setShowAddPaymentMethod] = useState(false);
  const [processing, setProcessing] = useState(false);

  // Refund states
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [refundPayment, setRefundPayment] = useState(null);
  const [refundInvoice, setRefundInvoice] = useState(null);
  const [selectedRefundDetails, setSelectedRefundDetails] = useState(null);

  // Load initial data
  useEffect(() => {
    loadBillingData();
  }, []);

  const loadBillingData = async () => {
    try {
      setLoading(true);
      setError('');

      const [profileRes, invoicesRes, receiptsRes, methodsRes] = await Promise.all([
        axios.get(`${getApiBase()}/billing/profile`, { headers: getAuthHeaders() }).catch(() => null),
        axios.get(`${getApiBase()}/billing/invoices`, { headers: getAuthHeaders() }).catch(() => ({ data: { invoices: [] } })),
        axios.get(`${getApiBase()}/billing/receipts`, { headers: getAuthHeaders() }).catch(() => ({ data: { receipts: [] } })),
        axios.get(`${getApiBase()}/billing/payment-methods`, { headers: getAuthHeaders() }).catch(() => ({ data: [] })),
      ]);

      if (profileRes?.data) {
        setBillingProfile(profileRes.data);
      }
      setInvoices(invoicesRes?.data?.invoices || []);
      setReceipts(receiptsRes?.data?.receipts || []);
      setPaymentMethods(methodsRes?.data || []);

    } catch (err) {
      console.error('Error loading billing data:', err);
      setError('Failed to load billing data');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(amount / 100);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'bg-gray-100 text-gray-800',
      issued: 'bg-blue-100 text-blue-800',
      paid: 'bg-green-100 text-green-800',
      partially_paid: 'bg-yellow-100 text-yellow-800',
      overdue: 'bg-red-100 text-red-800',
      voided: 'bg-gray-100 text-gray-500',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  // =========================================================================
  // Billing Profile Section
  // =========================================================================

  const BillingProfileSection = () => {
    const [editing, setEditing] = useState(false);
    const [formData, setFormData] = useState({
      company_name: '',
      billing_email: '',
      billing_phone: '',
      tax_id: '',
      billing_address: {
        line1: '',
        line2: '',
        city: '',
        state: '',
        postal_code: '',
        country: 'US',
      },
    });

    useEffect(() => {
      if (billingProfile) {
        setFormData({
          company_name: billingProfile.company_name || '',
          billing_email: billingProfile.billing_email || '',
          billing_phone: billingProfile.billing_phone || '',
          tax_id: billingProfile.tax_id || '',
          billing_address: billingProfile.billing_address || {
            line1: '',
            line2: '',
            city: '',
            state: '',
            postal_code: '',
            country: 'US',
          },
        });
      }
    }, [billingProfile]);

    const handleSaveProfile = async () => {
      try {
        setProcessing(true);
        const endpoint = billingProfile ? 'PUT' : 'POST';
        const response = await axios({
          method: endpoint,
          url: `${getApiBase()}/billing/profile`,
          data: formData,
          headers: getAuthHeaders(),
        });
        setBillingProfile(response.data);
        setEditing(false);
        setSuccess('Billing profile saved successfully');
        setTimeout(() => setSuccess(''), 3000);
      } catch (err) {
        setError(err.response?.data?.detail || 'Failed to save billing profile');
      } finally {
        setProcessing(false);
      }
    };

    if (!billingProfile && !editing) {
      return (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-center py-8">
            <Building2 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Billing Profile</h3>
            <p className="text-gray-500 mb-4">Set up your billing profile to start creating invoices.</p>
            <button
              onClick={() => setEditing(true)}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Billing Profile
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b flex justify-between items-center">
          <h3 className="text-lg font-medium text-gray-900">Billing Profile</h3>
          {!editing && (
            <button
              onClick={() => setEditing(true)}
              className="text-blue-600 hover:text-blue-700 text-sm font-medium"
            >
              Edit
            </button>
          )}
        </div>

        <div className="p-6">
          {editing ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Company Name</label>
                  <input
                    type="text"
                    value={formData.company_name}
                    onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Billing Email</label>
                  <input
                    type="email"
                    value={formData.billing_email}
                    onChange={(e) => setFormData({ ...formData, billing_email: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <input
                    type="tel"
                    value={formData.billing_phone}
                    onChange={(e) => setFormData({ ...formData, billing_phone: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tax ID</label>
                  <input
                    type="text"
                    value={formData.tax_id}
                    onChange={(e) => setFormData({ ...formData, tax_id: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Address Line 1</label>
                <input
                  type="text"
                  value={formData.billing_address.line1}
                  onChange={(e) => setFormData({
                    ...formData,
                    billing_address: { ...formData.billing_address, line1: e.target.value }
                  })}
                  className="w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                  <input
                    type="text"
                    value={formData.billing_address.city}
                    onChange={(e) => setFormData({
                      ...formData,
                      billing_address: { ...formData.billing_address, city: e.target.value }
                    })}
                    className="w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                  <input
                    type="text"
                    value={formData.billing_address.state}
                    onChange={(e) => setFormData({
                      ...formData,
                      billing_address: { ...formData.billing_address, state: e.target.value }
                    })}
                    className="w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Postal Code</label>
                  <input
                    type="text"
                    value={formData.billing_address.postal_code}
                    onChange={(e) => setFormData({
                      ...formData,
                      billing_address: { ...formData.billing_address, postal_code: e.target.value }
                    })}
                    className="w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  onClick={() => setEditing(false)}
                  className="px-4 py-2 text-gray-700 border rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveProfile}
                  disabled={processing}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {processing ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
                </button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-6">
              <div>
                <p className="text-sm text-gray-500">Company</p>
                <p className="font-medium">{billingProfile?.company_name || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Email</p>
                <p className="font-medium">{billingProfile?.billing_email || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Phone</p>
                <p className="font-medium">{billingProfile?.billing_phone || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Tax ID</p>
                <p className="font-medium">{billingProfile?.tax_id || '-'}</p>
              </div>
              <div className="col-span-2">
                <p className="text-sm text-gray-500">Address</p>
                <p className="font-medium">
                  {billingProfile?.billing_address?.line1 && (
                    <>
                      {billingProfile.billing_address.line1}<br />
                      {billingProfile.billing_address.city}, {billingProfile.billing_address.state} {billingProfile.billing_address.postal_code}
                    </>
                  )}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // =========================================================================
  // Invoices & Receipts Section (Read-only - invoices created by admins)
  // =========================================================================

  const InvoicesSection = () => {
    const [downloadingPdf, setDownloadingPdf] = useState(false);
    const [selectedReceipt, setSelectedReceipt] = useState(null);

    const downloadInvoicePdf = async (invoice) => {
      try {
        setDownloadingPdf(true);
        const response = await axios.get(
          `${getApiBase()}/billing/invoices/${invoice.id}/pdf`,
          {
            headers: getAuthHeaders(),
            responseType: 'blob',
          }
        );

        const contentDisposition = response.headers['content-disposition'];
        let filename = `invoice-${invoice.invoice_number || invoice.provisional_number || invoice.id}.pdf`;
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
          if (filenameMatch) {
            filename = filenameMatch[1];
          }
        }

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (err) {
        console.error('Error downloading PDF:', err);
        setError(err.response?.data?.detail || 'Failed to download invoice PDF');
      } finally {
        setDownloadingPdf(false);
      }
    };

    const downloadReceiptPdf = async (receipt) => {
      try {
        setDownloadingPdf(true);
        const response = await axios.get(
          `${getApiBase()}/billing/receipts/${receipt.id}/pdf`,
          {
            headers: getAuthHeaders(),
            responseType: 'blob',
          }
        );

        const contentDisposition = response.headers['content-disposition'];
        let filename = `receipt-${receipt.receipt_number}.pdf`;
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
          if (filenameMatch) {
            filename = filenameMatch[1];
          }
        }

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (err) {
        console.error('Error downloading PDF:', err);
        setError(err.response?.data?.detail || 'Failed to download receipt PDF');
      } finally {
        setDownloadingPdf(false);
      }
    };

    const handlePayInvoice = async (invoiceId, paymentMethodId) => {
      try {
        setProcessing(true);
        const response = await axios.post(
          `${getApiBase()}/billing/invoices/${invoiceId}/pay`,
          null,
          {
            headers: getAuthHeaders(),
            params: { payment_method_id: paymentMethodId },
          }
        );

        setInvoices(invoices.map(inv =>
          inv.id === invoiceId
            ? { ...inv, status: response.data.invoice_status, amount_paid: inv.total }
            : inv
        ));

        // Reload to get new receipt
        loadBillingData();

        setSuccess('Payment processed successfully');
        setTimeout(() => setSuccess(''), 3000);
        setSelectedInvoice(null);
      } catch (err) {
        setError(err.response?.data?.detail || 'Payment failed');
      } finally {
        setProcessing(false);
      }
    };

    const formatDateTime = (dateStr) => {
      if (!dateStr) return '-';
      return new Date(dateStr).toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    };

    return (
      <div className="space-y-6">
        {/* Sub-tabs for Invoices/Receipts */}
        <div className="bg-white rounded-lg shadow">
          <div className="border-b px-4">
            <nav className="flex space-x-6">
              <button
                onClick={() => setBillingSubTab('invoices')}
                className={`py-3 px-1 border-b-2 font-medium text-sm ${
                  billingSubTab === 'invoices'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <FileText className="w-4 h-4 inline mr-2" />
                Invoices ({invoices.length})
              </button>
              <button
                onClick={() => setBillingSubTab('receipts')}
                className={`py-3 px-1 border-b-2 font-medium text-sm ${
                  billingSubTab === 'receipts'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Receipt className="w-4 h-4 inline mr-2" />
                Receipts ({receipts.length})
              </button>
            </nav>
          </div>
        </div>

        {billingSubTab === 'invoices' && (
          <>
            {/* Invoice List */}
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b flex justify-between items-center">
                <h3 className="text-lg font-medium text-gray-900">Invoices</h3>
                <button
                  onClick={loadBillingData}
                  className="text-gray-600 hover:text-gray-800 p-2"
                  title="Refresh"
                >
                  <RefreshCw className="w-4 h-4" />
                </button>
              </div>

              {invoices.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <FileText className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p>No invoices yet</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Invoice #</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Due Date</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {invoices.map((invoice) => (
                        <tr key={invoice.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {invoice.invoice_number || invoice.provisional_number || invoice.id}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(invoice.status)}`}>
                              {invoice.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {formatCurrency(invoice.total, invoice.currency)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatDate(invoice.due_date)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                            <div className="flex justify-end gap-2">
                              <button
                                onClick={() => downloadInvoicePdf(invoice)}
                                disabled={downloadingPdf}
                                className="text-gray-600 hover:text-gray-800 disabled:opacity-50"
                                title="Download PDF"
                              >
                                {downloadingPdf ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Download className="w-4 h-4" />
                                )}
                              </button>
                              <button
                                onClick={() => setSelectedInvoice(invoice)}
                                className="text-blue-600 hover:text-blue-800"
                                title="View"
                              >
                                <Eye className="w-4 h-4" />
                              </button>
                              {invoice.status === 'issued' && (
                                <button
                                  onClick={() => setSelectedInvoice(invoice)}
                                  className="text-green-600 hover:text-green-800"
                                  title="Pay"
                                >
                                  <DollarSign className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Invoice Detail Modal */}
            {selectedInvoice && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70]">
                <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
                  <div className="px-6 py-4 border-b flex justify-between items-center">
                    <h3 className="text-lg font-medium text-gray-900">
                      Invoice {selectedInvoice.invoice_number || selectedInvoice.provisional_number || selectedInvoice.id}
                    </h3>
                    <button onClick={() => setSelectedInvoice(null)} className="text-gray-400 hover:text-gray-600">
                      <X className="w-5 h-5" />
                    </button>
                  </div>

                  <div className="p-6">
                    <div className="grid grid-cols-2 gap-4 mb-6">
                      <div>
                        <p className="text-sm text-gray-500">Status</p>
                        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(selectedInvoice.status)}`}>
                          {selectedInvoice.status}
                        </span>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Due Date</p>
                        <p className="font-medium">{formatDate(selectedInvoice.due_date)}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Total</p>
                        <p className="font-medium text-lg">{formatCurrency(selectedInvoice.total, selectedInvoice.currency)}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Amount Paid</p>
                        <p className="font-medium">{formatCurrency(selectedInvoice.amount_paid || 0, selectedInvoice.currency)}</p>
                      </div>
                    </div>

                    {selectedInvoice.line_items && (
                      <div className="mb-6">
                        <h4 className="text-sm font-medium text-gray-700 mb-2">Line Items</h4>
                        <table className="min-w-full">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Description</th>
                              <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Qty</th>
                              <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Price</th>
                              <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedInvoice.line_items.map((item, idx) => (
                              <tr key={idx} className="border-t">
                                <td className="px-3 py-2 text-sm">{item.description}</td>
                                <td className="px-3 py-2 text-sm text-right">{item.quantity}</td>
                                <td className="px-3 py-2 text-sm text-right">{formatCurrency(item.unit_price)}</td>
                                <td className="px-3 py-2 text-sm text-right font-medium">
                                  {formatCurrency(item.quantity * item.unit_price)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {selectedInvoice.status === 'issued' && paymentMethods.length > 0 && (
                      <div className="border-t pt-4">
                        <h4 className="text-sm font-medium text-gray-700 mb-3">Pay Invoice</h4>
                        <div className="space-y-2">
                          {paymentMethods.map((pm) => (
                            <button
                              key={pm.id}
                              onClick={() => handlePayInvoice(selectedInvoice.id, pm.id)}
                              disabled={processing}
                              className="w-full flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50"
                            >
                              <div className="flex items-center">
                                <CreditCard className="w-5 h-5 mr-3 text-gray-400" />
                                <span className="font-medium">{pm.card_brand} ****{pm.card_last4}</span>
                                {pm.is_default && (
                                  <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded">Default</span>
                                )}
                              </div>
                              <span className="text-blue-600">Pay {formatCurrency(selectedInvoice.total - (selectedInvoice.amount_paid || 0))}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {selectedInvoice.status === 'issued' && paymentMethods.length === 0 && (
                      <div className="border-t pt-4">
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
                          <AlertCircle className="w-6 h-6 text-yellow-600 mx-auto mb-2" />
                          <p className="text-yellow-800">Add a payment method to pay this invoice</p>
                          <button
                            onClick={() => { setSelectedInvoice(null); setActiveTab('payments'); }}
                            className="mt-2 text-blue-600 hover:text-blue-800 font-medium"
                          >
                            Add Payment Method
                          </button>
                        </div>
                      </div>
                    )}

                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {billingSubTab === 'receipts' && (
          <>
            {/* Receipt List */}
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b flex justify-between items-center">
                <h3 className="text-lg font-medium text-gray-900">Receipts</h3>
                <button
                  onClick={loadBillingData}
                  className="text-gray-600 hover:text-gray-800 p-2"
                  title="Refresh"
                >
                  <RefreshCw className="w-4 h-4" />
                </button>
              </div>

              {receipts.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <Receipt className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p>No receipts yet</p>
                  <p className="text-sm mt-1">Receipts are generated when you make a payment</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Receipt #</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Payment Method</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {receipts.map((receipt) => (
                        <tr key={receipt.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {receipt.receipt_number}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatDate(receipt.paid_at)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {formatCurrency(receipt.amount, receipt.currency)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {receipt.payment_method_brand ? (
                              <span className="capitalize">
                                {receipt.payment_method_brand} ****{receipt.payment_method_last4}
                              </span>
                            ) : (
                              <span>Card</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                            <div className="flex justify-end gap-2">
                              <button
                                onClick={() => downloadReceiptPdf(receipt)}
                                disabled={downloadingPdf}
                                className="text-gray-600 hover:text-gray-800 disabled:opacity-50"
                                title="Download PDF"
                              >
                                {downloadingPdf ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Download className="w-4 h-4" />
                                )}
                              </button>
                              <button
                                onClick={() => setSelectedReceipt(receipt)}
                                className="text-blue-600 hover:text-blue-800"
                                title="View"
                              >
                                <Eye className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Receipt Detail Modal */}
            {selectedReceipt && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70]">
                <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4">
                  <div className="px-6 py-4 border-b flex justify-between items-center">
                    <h3 className="text-lg font-medium text-gray-900">
                      Receipt {selectedReceipt.receipt_number}
                    </h3>
                    <button onClick={() => setSelectedReceipt(null)} className="text-gray-400 hover:text-gray-600">
                      <X className="w-5 h-5" />
                    </button>
                  </div>

                  <div className="p-6">
                    <div className="text-center mb-6">
                      <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                        <Check className="w-8 h-8 text-green-600" />
                      </div>
                      <p className="text-lg font-semibold text-gray-900">Payment Successful</p>
                    </div>

                    <div className="bg-gray-50 rounded-lg p-4 mb-4">
                      <div className="text-center">
                        <p className="text-sm text-gray-500">Amount Paid</p>
                        <p className="text-3xl font-bold text-gray-900">
                          {formatCurrency(selectedReceipt.amount, selectedReceipt.currency)}
                        </p>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-gray-500">Date</span>
                        <span className="font-medium">{formatDateTime(selectedReceipt.paid_at)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Payment Method</span>
                        <span className="font-medium capitalize">
                          {selectedReceipt.payment_method_brand || 'Card'}
                          {selectedReceipt.payment_method_last4 && ` ****${selectedReceipt.payment_method_last4}`}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Description</span>
                        <span className="font-medium">{selectedReceipt.description || '-'}</span>
                      </div>
                      {selectedReceipt.stripe_payment_intent_id && (
                        <div className="flex justify-between">
                          <span className="text-gray-500">Reference</span>
                          <span className="font-mono text-sm">
                            ...{selectedReceipt.stripe_payment_intent_id.slice(-12)}
                          </span>
                        </div>
                      )}
                    </div>

                    <div className="mt-6 flex gap-3">
                      <button
                        onClick={() => downloadReceiptPdf(selectedReceipt)}
                        disabled={downloadingPdf}
                        className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                      >
                        {downloadingPdf ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Download className="w-4 h-4" />
                        )}
                        Download PDF
                      </button>
                      <button
                        onClick={() => setSelectedReceipt(null)}
                        className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                      >
                        Close
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  // =========================================================================
  // Payment Methods Section
  // =========================================================================

  // Card Entry Form Component (must be inside Elements provider)
  const CardEntryForm = ({ clientSecret, onSuccess, onCancel }) => {
    const stripe = useStripe();
    const elements = useElements();
    const [cardError, setCardError] = useState('');
    const [processing, setProcessing] = useState(false);

    const handleSubmit = async (e) => {
      e.preventDefault();
      if (!stripe || !elements) return;

      setProcessing(true);
      setCardError('');

      try {
        const { error, setupIntent } = await stripe.confirmCardSetup(clientSecret, {
          payment_method: {
            card: elements.getElement(CardElement),
          },
        });

        if (error) {
          setCardError(error.message);
        } else if (setupIntent.status === 'succeeded') {
          // Save the payment method to our backend
          await axios.post(
            `${getApiBase()}/billing/payment-methods`,
            null,
            {
              headers: getAuthHeaders(),
              params: {
                stripe_payment_method_id: setupIntent.payment_method,
                set_as_default: true,
              },
            }
          );
          onSuccess();
        }
      } catch (err) {
        setCardError(err.response?.data?.detail || 'Failed to save payment method');
      } finally {
        setProcessing(false);
      }
    };

    const cardElementOptions = {
      style: {
        base: {
          fontSize: '16px',
          color: '#424770',
          '::placeholder': {
            color: '#aab7c4',
          },
        },
        invalid: {
          color: '#9e2146',
        },
      },
    };

    return (
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="p-4 border rounded-lg bg-gray-50">
          <CardElement options={cardElementOptions} />
        </div>
        {cardError && (
          <div className="text-red-600 text-sm">{cardError}</div>
        )}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
          <strong>Test Cards:</strong> Use 4242 4242 4242 4242 with any future expiry and any CVC.
        </div>
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={processing}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!stripe || processing}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center"
          >
            {processing && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {processing ? 'Saving...' : 'Save Card'}
          </button>
        </div>
      </form>
    );
  };

  const PaymentMethodsSection = () => {
    const [showCardForm, setShowCardForm] = useState(false);
    const [clientSecret, setClientSecret] = useState(null);
    const [formLoading, setFormLoading] = useState(false);
    const [bankAccount, setBankAccount] = useState(null);
    const [bankLoading, setBankLoading] = useState(true);

    // Load existing bank account (Stripe ACH payment method)
    useEffect(() => {
      loadBankAccount();
    }, []);

    const loadBankAccount = async () => {
      try {
        setBankLoading(true);
        const orgId = currentUser?.organization_id;
        if (!orgId) return;
        const response = await axios.get(
          `${getApiBase()}/billing/ach/bank-account/${orgId}`,
          { headers: getAuthHeaders() }
        );
        if (response.data?.data) {
          setBankAccount(response.data.data);
        }
      } catch (err) {
        // 404 means no bank account yet, which is fine
        if (err.response?.status !== 404) {
          console.error('Error loading bank account:', err);
        }
      } finally {
        setBankLoading(false);
      }
    };

    const handleAddBankAccount = async () => {
      const orgId = currentUser?.organization_id;
      if (!orgId) {
        setError('Organization not found');
        return;
      }

      try {
        setFormLoading(true);
        setError('');

        // Create a Stripe SetupIntent for ACH bank account
        const response = await axios.post(
          `${getApiBase()}/billing/ach/bank-account/${orgId}/setup`,
          {},
          { headers: getAuthHeaders() }
        );

        const { client_secret } = response.data.data;

        const stripeInstance = await stripePromise;
        if (!stripeInstance) {
          setError('Stripe has not loaded yet. Please refresh and try again.');
          return;
        }

        // Use Stripe.js to collect bank account via Financial Connections
        const { error: stripeError, setupIntent } = await stripeInstance.collectBankAccountForSetup({
          clientSecret: client_secret,
          params: {
            payment_method_type: 'us_bank_account',
            payment_method_data: {
              billing_details: {
                name: billingProfile?.company_name || currentUser?.first_name || '',
                email: billingProfile?.billing_email || currentUser?.email || '',
              },
            },
          },
        });

        if (stripeError) {
          setError(stripeError.message || 'Failed to set up bank account');
          return;
        }

        if (setupIntent.status === 'requires_payment_method') {
          // User cancelled the Financial Connections flow
          setError('Bank account setup was cancelled');
          return;
        }

        // If status is requires_confirmation, confirm the SetupIntent
        if (setupIntent.status === 'requires_confirmation') {
          const { error: confirmError } = await stripeInstance.confirmUsBankAccountSetup(client_secret);
          if (confirmError) {
            setError(confirmError.message || 'Failed to confirm bank account');
            return;
          }
        }

        // Save the payment method to our backend via the existing billing endpoint
        if (setupIntent.payment_method) {
          try {
            await axios.post(
              `${getApiBase()}/billing/payment-methods?stripe_payment_method_id=${setupIntent.payment_method}&set_as_default=false`,
              {},
              { headers: getAuthHeaders() }
            );
          } catch (attachErr) {
            console.error('Failed to save payment method:', attachErr);
            setError('Bank account was linked to Stripe but could not be saved. Please try again or contact support.');
            return;
          }
        }

        // Reload bank account data
        await loadBankAccount();
        setSuccess('Bank account added successfully via Stripe');
        setTimeout(() => setSuccess(''), 3000);
      } catch (err) {
        setError(err.response?.data?.detail || 'Failed to set up bank account');
      } finally {
        setFormLoading(false);
      }
    };

    const handleRemoveBankAccount = async () => {
      if (!window.confirm('Are you sure you want to remove this bank account?')) return;

      const orgId = currentUser?.organization_id;
      if (!orgId) return;

      try {
        setProcessing(true);
        await axios.delete(
          `${getApiBase()}/billing/ach/bank-account/${orgId}`,
          { headers: getAuthHeaders() }
        );
        setBankAccount(null);
        setSuccess('Bank account removed');
        setTimeout(() => setSuccess(''), 3000);
      } catch (err) {
        setError(err.response?.data?.detail || 'Failed to remove bank account');
      } finally {
        setProcessing(false);
      }
    };

    const handleAddPaymentMethod = async () => {
      if (!billingProfile?.id) {
        setError('Please create a billing profile first');
        return;
      }

      try {
        setFormLoading(true);
        setError('');
        const response = await axios.post(
          `${getApiBase()}/billing/payment-methods/setup-intent`,
          {},
          { headers: getAuthHeaders() }
        );
        setClientSecret(response.data.client_secret);
        setShowCardForm(true);
      } catch (err) {
        setError(err.response?.data?.detail || 'Failed to initialize card form');
      } finally {
        setFormLoading(false);
      }
    };

    const handleCardSuccess = () => {
      setShowCardForm(false);
      setClientSecret(null);
      setSuccess('Payment method added successfully');
      setTimeout(() => setSuccess(''), 3000);
      loadBillingData(); // Refresh payment methods
    };

    const handleCardCancel = () => {
      setShowCardForm(false);
      setClientSecret(null);
    };

    const handleSetDefault = async (paymentMethodId) => {
      try {
        setProcessing(true);
        await axios.post(
          `${getApiBase()}/billing/payment-methods/${paymentMethodId}/default`,
          {},
          { headers: getAuthHeaders() }
        );
        setPaymentMethods(paymentMethods.map(pm => ({
          ...pm,
          is_default: pm.id === paymentMethodId,
        })));
        setSuccess('Default payment method updated');
        setTimeout(() => setSuccess(''), 3000);
      } catch (err) {
        setError(err.response?.data?.detail || 'Failed to set default payment method');
      } finally {
        setProcessing(false);
      }
    };

    const handleRemove = async (paymentMethodId) => {
      if (!window.confirm('Are you sure you want to remove this payment method?')) return;

      try {
        setProcessing(true);
        await axios.delete(
          `${getApiBase()}/billing/payment-methods/${paymentMethodId}`,
          { headers: getAuthHeaders() }
        );
        setPaymentMethods(paymentMethods.filter(pm => pm.id !== paymentMethodId));
        setSuccess('Payment method removed');
        setTimeout(() => setSuccess(''), 3000);
      } catch (err) {
        setError(err.response?.data?.detail || 'Failed to remove payment method');
      } finally {
        setProcessing(false);
      }
    };

    const hasAnyForm = showCardForm;

    return (
      <div className="space-y-6">
        {/* Credit Cards Section */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b flex justify-between items-center">
            <div className="flex items-center">
              <CreditCard className="w-5 h-5 text-gray-500 mr-2" />
              <h3 className="text-lg font-medium text-gray-900">Credit Cards</h3>
            </div>
            {!showCardForm && (
              <button
                onClick={handleAddPaymentMethod}
                disabled={formLoading || processing}
                className="inline-flex items-center px-3 py-1.5 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {formLoading ? (
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                ) : (
                  <Plus className="w-4 h-4 mr-1" />
                )}
                Add Card
              </button>
            )}
          </div>

          {/* Card Entry Form */}
          {showCardForm && clientSecret && (
            <div className="p-6 border-b bg-gray-50">
              <h4 className="font-medium mb-4">Add Credit Card</h4>
              <Elements stripe={stripePromise} options={{ clientSecret }}>
                <CardEntryForm
                  clientSecret={clientSecret}
                  onSuccess={handleCardSuccess}
                  onCancel={handleCardCancel}
                />
              </Elements>
            </div>
          )}

          {paymentMethods.length === 0 && !showCardForm ? (
            <div className="p-8 text-center text-gray-500">
              <CreditCard className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>No credit cards added</p>
              <p className="text-sm mt-2">Add a credit card to pay invoices</p>
            </div>
          ) : paymentMethods.length > 0 && (
            <div className="p-6 space-y-3">
              {paymentMethods.map((pm) => (
                <div
                  key={pm.id}
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50"
                >
                  <div className="flex items-center">
                    <CreditCard className="w-8 h-8 text-gray-400 mr-4" />
                    <div>
                      <p className="font-medium">
                        {pm.card_brand || pm.brand} ****{pm.card_last4 || pm.last_four}
                        {pm.is_default && (
                          <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded">Default</span>
                        )}
                      </p>
                      <p className="text-sm text-gray-500">Expires {pm.card_exp_month || pm.exp_month}/{pm.card_exp_year || pm.exp_year}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {!pm.is_default && (
                      <button
                        onClick={() => handleSetDefault(pm.id)}
                        className="p-2 text-gray-400 hover:text-blue-600"
                        title="Set as default"
                      >
                        <Star className="w-4 h-4" />
                      </button>
                    )}
                    <button
                      onClick={() => handleRemove(pm.id)}
                      className="p-2 text-gray-400 hover:text-red-600"
                      title="Remove"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ACH Bank Account Section */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b flex justify-between items-center">
            <div className="flex items-center">
              <Landmark className="w-5 h-5 text-gray-500 mr-2" />
              <h3 className="text-lg font-medium text-gray-900">ACH Bank Account</h3>
            </div>
            {!bankAccount && !bankLoading && (
              <button
                onClick={handleAddBankAccount}
                disabled={formLoading || processing || showCardForm}
                className="inline-flex items-center px-3 py-1.5 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {formLoading ? (
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                ) : (
                  <Plus className="w-4 h-4 mr-1" />
                )}
                {formLoading ? 'Connecting...' : 'Add Bank Account'}
              </button>
            )}
          </div>

          {/* Existing Bank Account Display */}
          {bankLoading ? (
            <div className="p-8 text-center text-gray-500">
              <Loader2 className="w-8 h-8 mx-auto mb-2 animate-spin text-gray-300" />
              <p className="text-sm">Loading bank account...</p>
            </div>
          ) : bankAccount ? (
            <div className="p-6">
              <div className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50">
                <div className="flex items-center">
                  <Landmark className="w-8 h-8 text-green-500 mr-4" />
                  <div>
                    <p className="font-medium">
                      {bankAccount.display_name || bankAccount.brand || 'Bank Account'}
                      {bankAccount.last_four && ` ****${bankAccount.last_four}`}
                      {bankAccount.status === 'active' && (
                        <span className="ml-2 text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded">Active</span>
                      )}
                    </p>
                    <p className="text-sm text-gray-500">
                      ACH Direct Debit &middot; {bankAccount.currency || 'USD'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleRemoveBankAccount}
                    className="p-2 text-gray-400 hover:text-red-600"
                    title="Remove bank account"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="p-8 text-center text-gray-500">
              <Landmark className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>No bank account added</p>
              <p className="text-sm mt-2">Add a bank account for ACH direct debit payments via Stripe</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  // =========================================================================
  // Overview Section
  // =========================================================================

  const OverviewSection = () => {
    const unpaidInvoices = invoices.filter(inv => inv.status === 'issued' || inv.status === 'partially_paid');
    const totalDue = unpaidInvoices.reduce((sum, inv) => sum + (inv.total - (inv.amount_paid || 0)), 0);
    const paidInvoices = invoices.filter(inv => inv.status === 'paid');
    const totalPaid = paidInvoices.reduce((sum, inv) => sum + inv.total, 0);

    return (
      <div className="space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-3 bg-blue-100 rounded-lg">
                <FileText className="w-6 h-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-500">Total Invoices</p>
                <p className="text-2xl font-semibold">{invoices.length}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-3 bg-yellow-100 rounded-lg">
                <DollarSign className="w-6 h-6 text-yellow-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-500">Amount Due</p>
                <p className="text-2xl font-semibold">{formatCurrency(totalDue)}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-3 bg-green-100 rounded-lg">
                <Check className="w-6 h-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-500">Total Paid</p>
                <p className="text-2xl font-semibold">{formatCurrency(totalPaid)}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-3 bg-purple-100 rounded-lg">
                <CreditCard className="w-6 h-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-500">Payment Methods</p>
                <p className="text-2xl font-semibold">{paymentMethods.length}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Unpaid Invoices */}
        {unpaidInvoices.length > 0 && (
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b">
              <h3 className="text-lg font-medium text-gray-900">Unpaid Invoices</h3>
            </div>
            <div className="divide-y">
              {unpaidInvoices.slice(0, 5).map((invoice) => (
                <div key={invoice.id} className="px-6 py-4 flex justify-between items-center">
                  <div>
                    <p className="font-medium">{invoice.invoice_number || invoice.provisional_number || invoice.id}</p>
                    <p className="text-sm text-gray-500">Due {formatDate(invoice.due_date)}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-medium">{formatCurrency(invoice.total - (invoice.amount_paid || 0))}</p>
                    <button
                      onClick={() => { setSelectedInvoice(invoice); setActiveTab('invoices'); }}
                      className="text-blue-600 hover:text-blue-800 text-sm"
                    >
                      Pay Now
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Billing Profile Summary */}
        <BillingProfileSection />
      </div>
    );
  };

  // =========================================================================
  // Refunds Section
  // =========================================================================

  const RefundsSection = () => {
    const handleSelectRefund = (refund) => {
      setSelectedRefundDetails(refund);
    };

    const handleRefundRequested = () => {
      // Refresh billing data after refund request
      loadBillingData();
    };

    return (
      <div className="space-y-6">
        {/* Refund List */}
        <RefundList
          onSelectRefund={handleSelectRefund}
          isAdmin={false}
        />

        {/* Refund Details Modal */}
        {selectedRefundDetails && (
          <RefundDetails
            refund={selectedRefundDetails}
            onClose={() => setSelectedRefundDetails(null)}
            canApprove={false}
            currentUserId={currentUser?.id}
          />
        )}
      </div>
    );
  };

  // Function to request refund from an invoice/receipt
  const handleRequestRefund = async (invoice) => {
    try {
      // Get the payment associated with this invoice
      const response = await axios.get(
        `${getApiBase()}/billing/invoices/${invoice.id}/payments`,
        { headers: getAuthHeaders() }
      );

      const payments = response.data.payments || [];
      if (payments.length === 0) {
        setError('No payments found for this invoice');
        return;
      }

      // Use the most recent completed payment
      const payment = payments.find(p => p.status === 'succeeded') || payments[0];

      setRefundPayment(payment);
      setRefundInvoice(invoice);
      setShowRefundModal(true);
      setSelectedInvoice(null);
    } catch (err) {
      console.error('Error getting payments:', err);
      setError(err.response?.data?.detail || 'Failed to load payment information');
    }
  };

  // =========================================================================
  // ACH Activity Section
  // =========================================================================

  const ACHActivitySection = () => {
    const [achBalance, setAchBalance] = useState(null);
    const [achEntries, setAchEntries] = useState([]);
    const [achCharges, setAchCharges] = useState([]);
    const [achLoading, setAchLoading] = useState(true);
    const [achSubTab, setAchSubTab] = useState('balance');

    const orgId = currentUser?.organization_id;

    useEffect(() => {
      if (orgId) loadACHData();
    }, [orgId]);

    const loadACHData = async () => {
      try {
        setAchLoading(true);
        const [balanceRes, entriesRes, chargesRes] = await Promise.all([
          axios.get(`${getApiBase()}/billing/ach/balance/${orgId}`, { headers: getAuthHeaders() }).catch(() => null),
          axios.get(`${getApiBase()}/billing/ach/balance/${orgId}/entries?limit=50`, { headers: getAuthHeaders() }).catch(() => ({ data: { data: [] } })),
          axios.get(`${getApiBase()}/billing/ach/charges/${orgId}?limit=50`, { headers: getAuthHeaders() }).catch(() => ({ data: { data: [] } })),
        ]);
        if (balanceRes?.data?.data) setAchBalance(balanceRes.data.data);
        setAchEntries(entriesRes?.data?.data || []);
        setAchCharges(chargesRes?.data?.data || []);
      } catch (err) {
        console.error('Error loading ACH data:', err);
      } finally {
        setAchLoading(false);
      }
    };

    const getChargeStatusColor = (status) => {
      const colors = {
        pending: 'bg-yellow-100 text-yellow-800',
        processing: 'bg-blue-100 text-blue-800',
        succeeded: 'bg-green-100 text-green-800',
        failed: 'bg-red-100 text-red-800',
      };
      return colors[status?.toLowerCase()] || 'bg-gray-100 text-gray-800';
    };

    const getEntryTypeLabel = (type) => {
      const labels = {
        travel: 'Travel Booking',
        subscription: 'Subscription',
        adjustment: 'Adjustment',
        credit: 'Credit',
      };
      return labels[type] || type;
    };

    const getTriggerLabel = (trigger) => {
      const labels = {
        booking: 'At Booking',
        monthly_schedule: 'Monthly',
        threshold_reached: 'Threshold',
        manual: 'Manual',
      };
      return labels[trigger] || trigger;
    };

    if (achLoading) {
      return (
        <div className="flex items-center justify-center h-48">
          <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
        </div>
      );
    }

    const hasAnyData = achBalance || achEntries.length > 0 || achCharges.length > 0;

    if (!hasAnyData) {
      return (
        <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
          <Landmark className="w-12 h-12 mx-auto mb-4 text-gray-300" />
          <p>No ACH billing activity for your organization.</p>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {/* Balance Summary */}
        {achBalance && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="p-3 bg-blue-100 rounded-lg">
                  <DollarSign className="w-6 h-6 text-blue-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm text-gray-500">Current Balance</p>
                  <p className="text-2xl font-semibold">
                    {formatCurrency(achBalance.current_balance_cents || 0)}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="p-3 bg-green-100 rounded-lg">
                  <ArrowUpDown className="w-6 h-6 text-green-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm text-gray-500">Billing Mode</p>
                  <p className="text-lg font-semibold capitalize">
                    {(achBalance.billing_mode || '').replace(/_/g, ' ')}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="p-3 bg-purple-100 rounded-lg">
                  <Calendar className="w-6 h-6 text-purple-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm text-gray-500">Next Charge Date</p>
                  <p className="text-lg font-semibold">
                    {achBalance.next_monthly_charge_date
                      ? formatDate(achBalance.next_monthly_charge_date)
                      : 'N/A'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Sub-tabs */}
        <div className="bg-white rounded-lg shadow">
          <div className="border-b px-6">
            <nav className="flex space-x-6">
              {[
                { id: 'balance', label: 'Balance Entries' },
                { id: 'charges', label: 'Charge History' },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setAchSubTab(tab.id)}
                  className={`py-3 border-b-2 text-sm font-medium ${
                    achSubTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          {achSubTab === 'balance' && (
            <div className="overflow-x-auto">
              {achEntries.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <Banknote className="w-10 h-10 mx-auto mb-3 text-gray-300" />
                  <p>No balance entries yet</p>
                </div>
              ) : (
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {achEntries.map((entry) => (
                      <tr key={entry.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatDate(entry.created_at)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            entry.entry_type === 'travel' ? 'bg-indigo-100 text-indigo-800' :
                            entry.entry_type === 'subscription' ? 'bg-blue-100 text-blue-800' :
                            entry.entry_type === 'credit' ? 'bg-green-100 text-green-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {getEntryTypeLabel(entry.entry_type)}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-700">
                          {entry.description || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-medium">
                          {formatCurrency(entry.amount || 0)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            entry.settled ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {entry.settled ? 'Settled' : 'Pending'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {achSubTab === 'charges' && (
            <div className="overflow-x-auto">
              {achCharges.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <Banknote className="w-10 h-10 mx-auto mb-3 text-gray-300" />
                  <p>No charges yet</p>
                </div>
              ) : (
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Trigger</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {achCharges.map((charge) => (
                      <tr key={charge.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatDate(charge.created_at)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                          {getTriggerLabel(charge.trigger)}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-700">
                          {charge.description || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-medium">
                          {formatCurrency(charge.amount || 0)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getChargeStatusColor(charge.status)}`}>
                            {(charge.status || '').replace(/_/g, ' ')}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  // =========================================================================
  // Main Render
  // =========================================================================

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Overview', icon: Building2 },
    { id: 'invoices', label: 'Invoices & Receipts', icon: FileText },
    { id: 'payments', label: 'Payment Methods', icon: CreditCard },
    { id: 'ach', label: 'ACH Activity', icon: Landmark },
    { id: 'refunds', label: 'Refunds', icon: RotateCcw },
  ];

  return (
    <div className="bg-gray-50 min-h-screen max-w-6xl mx-auto px-4 py-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Billing</h1>
        <p className="text-gray-500">View invoices, manage payment methods, and billing settings</p>
      </div>

      {/* Alerts */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center text-red-800">
          <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0" />
          <span>{error}</span>
          <button onClick={() => setError('')} className="ml-auto text-red-600 hover:text-red-800">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {success && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center text-green-800">
          <Check className="w-5 h-5 mr-2 flex-shrink-0" />
          <span>{success}</span>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b mb-6">
        <nav className="flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <tab.icon className="w-4 h-4 mr-2" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && <OverviewSection />}
      {activeTab === 'invoices' && <InvoicesSection />}
      {activeTab === 'payments' && <PaymentMethodsSection />}
      {activeTab === 'ach' && <ACHActivitySection />}
      {activeTab === 'refunds' && <RefundsSection />}

      {/* Refund Request Modal */}
      <RefundRequestModal
        isOpen={showRefundModal}
        onClose={() => {
          setShowRefundModal(false);
          setRefundPayment(null);
          setRefundInvoice(null);
        }}
        payment={refundPayment}
        invoice={refundInvoice}
        onRefundRequested={() => {
          loadBillingData();
          setActiveTab('refunds');
        }}
      />
    </div>
  );
};

export default BillingDashboard;
